import {keys, cloneDeep, debounce, sortBy, values} from 'lodash';
import React, {
    forwardRef,
    useState,
    useMemo,
    ForwardRefRenderFunction,
    useCallback
} from 'react';
import {toast, Collapse, Checkbox} from 'acud';
import HooksForm from '../../../../components/biz/HooksForm';
import useForm from '../../../../hooks/biz/form';

import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';

import './index.less';

const {Panel} = Collapse;
const CheckboxGroup = Checkbox.Group;

const exportToExcel = async (info: any, fileName: string) => {
    const link = document.createElement('a');
    let blob = new Blob([info], {
        type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    link.style.display = 'none';
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const controls = (props, _, customFunctions) => {
    const {actions, attributeInfo = {}, attributeOptions, isGroup, groupAttributeInfo, selectMode} = props || {};
    const canExportAll = !!actions?.exportAll;
    const {indeterminate, checkAll, checkedList} = attributeInfo;
    const keyList = keys(groupAttributeInfo);
    return [
        {
            type: 'radio',
            name: 'type',
            label: '导出方式',
            defaultValue: canExportAll ? 'all' : 'show',
            config: {
                options: [
                    ...canExportAll ? [{label: '全量导出', value: 'all'}] : [],
                    {label: selectMode === 'none' ? '当前显示数据' : '已选中数据', value: 'show'}
                ]
            },
            rules: [{required: true, message: '请选择导导出模式'}]
        },
        {
            type: 'radio',
            name: 'way',
            label: '导出字段',
            defaultValue: 'all',
            config: {
                options: [
                    {label: '全部导出', value: 'all'},
                    {label: '自定义导出', value: 'custom'}
                ]
            },
            rules: [{required: true, message: '请选择导导出模式'}],
            onDisplay: [
                {
                    name: 'type',
                    content: [
                        {exp: '===', value: 'show'}
                    ]
                }
            ]
        },
        ...isGroup ? [
            {
                type: 'custom',
                name: 'attributes',
                label: '导出字段',
                content: (
                    <Collapse defaultActiveKey={keyList}>
                        {keyList.map(key => {
                            const info = groupAttributeInfo[key];
                            return (
                                <Panel
                                    key={key}
                                    header={
                                        <Checkbox
                                            indeterminate={info.indeterminate}
                                            checked={info.checkAll}
                                            onChange={e => customFunctions.onCheckAllChangeGruop(key, e.target.checked)}
                                        >
                                            {key}
                                        </Checkbox>
                                    }
                                >
                                    <Checkbox
                                        className="attributes-checkall"
                                        indeterminate={info.indeterminate}
                                        checked={info.checkAll}
                                        onChange={e => customFunctions.onCheckAllChangeGruop(key, e.target.checked)}
                                    >
                                        {key}
                                    </Checkbox>
                                    <CheckboxGroup
                                        options={info.options}
                                        value={info.checkedList}
                                        onChange={list => customFunctions.onChangeGroup(key, list)}
                                    />
                                </Panel>
                            );
                        })
                        }
                    </Collapse>
                ),
                rules: [{validator: customFunctions.checkAttributes}],
                onDisplay: [
                    {
                        name: 'way',
                        content: [
                            {exp: '===', value: 'custom'}
                        ]
                    }
                ]
            }
        ] : [
            {
                type: 'custom',
                name: 'attributes',
                label: '导出字段',
                content: (
                    <div className='download-form-attributes'>
                        <Checkbox
                            indeterminate={indeterminate}
                            onChange={customFunctions.onCheckAllChangeAttributes}
                            checked={checkAll}
                            style={{marginRight: 16}}
                        >
                            全选
                        </Checkbox>
                        <CheckboxGroup
                            style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4}}
                            options={attributeOptions}
                            value={checkedList}
                            onChange={customFunctions.onChangeAttributes}
                        />
                    </div>
                ),
                rules: [{validator: customFunctions.checkAttributes}],
                onDisplay: [
                    {
                        name: 'way',
                        content: [
                            {exp: '===', value: 'custom'}
                        ]
                    }
                ]
            }
        ]
    ];
};
interface GroupAttributeItem {
    indeterminate: boolean;
    checkAll: boolean;
    checkedList: any[];
    options: Array<{label: string; value: any; [key: string]: any}>;
}
interface GroupAttributeInfo {
    [key: string]: GroupAttributeItem;
}
interface TagFormProps {
    payload: {
        actions: any;
        options: any;
        dataSource: any[];
        selectMode: string;
        fileName: string;
        pageData: any[];
        selectedRowKeys: string[];
        filterInfo: object;
        reload: () => void;
        hideDialog: () => void;
    };
};
const UpdateSceneForm: ForwardRefRenderFunction<unknown, TagFormProps> = (props, ref) => {
    const {payload} = props;
    const {actions, options: tableInfo, dataSource, selectMode, fileName, reload, hideDialog} = payload;
    // eslint-disable-next-line max-len
    const attributeOptions = (tableInfo?.tableConfig?.columns || []).map((item: any) => ({label: item.title, value: item.dataIndex}));
    const [attributeInfo, setAttributeInfo] = useState({
        indeterminate: false,
        checkAll: true,
        checkedList: attributeOptions.map(a => a.value)
    });
    const onCheckAllChangeAttributes = useCallback((e: any) => {
        setAttributeInfo({
            indeterminate: false,
            checkAll: e.target.checked,
            checkedList: e.target.checked ? attributeOptions.map(a => a.value) : []
        });
    }, [attributeOptions]);
    const onChangeAttributes = useCallback((list: any[]) => {
        setAttributeInfo({
            indeterminate: !!list.length && list.length < attributeOptions.length,
            checkAll: list.length === attributeOptions.length,
            checkedList: list
        });
    }, [attributeOptions]);

    const isGroup = (tableInfo?.tableConfig?.columns || []).some((item: any) => !!item.groupName);
    const groupAttributeMap = (tableInfo?.tableConfig?.columns || []).reduce((map, item) => {
        if (!item.groupName) {
            return map;
        }
        if (!map[item.groupName]) {
            map[item.groupName] = {
                indeterminate: false,
                checkAll: true,
                checkedList: [],
                options: []
            };
        };
        map[item.groupName].options = sortBy([
            ...map[item.groupName].options,
            {
                label: item.title,
                value: item.dataIndex,
                ...item
            }
        ], ['sortIndex']);
        map[item.groupName].checkedList.push(item.dataIndex);
        return map;
    }, {});
    const [groupAttributeInfo, setGroupAttributeInfo] = useState<GroupAttributeInfo>(groupAttributeMap);
    const onCheckAllChangeGruop = useCallback((key: string, checked: boolean) => {
        const groupMap = cloneDeep(groupAttributeInfo);
        groupMap[key].checkAll = checked;
        groupMap[key].checkedList = checked ? groupMap[key].options.map(opt => opt.value) : [];
        groupMap[key].indeterminate = false;
        setGroupAttributeInfo(groupMap);
    }, [groupAttributeInfo]);
    const onChangeGroup = useCallback((key: string, list: any[]) => {
        const groupMap = cloneDeep(groupAttributeInfo);
        groupMap[key].checkAll = list.length === groupMap[key].options.length;
        groupMap[key].checkedList = list;
        groupMap[key].indeterminate = !!list.length && list.length < groupMap[key].options.length,
        setGroupAttributeInfo(groupMap);
    }, [groupAttributeInfo]);

    const downloadExcel = (data: any[], fileName: string, headers?: {label: string, key: any, groupName: string}[]) => {
        if (headers) {
            // 创建工作簿
            const wb = XLSX.utils.book_new();
            // 准备数据 - 多层表头
            let headerKey = '';
            let header = [] as string[];
            const headerGroup = headers.reduce((acc, curr: {label: string, key: any, groupName: string}) => {
                if (acc.length === 0) {
                    acc.push(curr.groupName);
                    headerKey = curr.groupName;
                }
                else if (headerKey === curr.groupName) {
                    acc.push('');
                }
                else if (headerKey !== curr.groupName) {
                    acc.push(curr.groupName);
                    headerKey = curr.groupName;
                }
                header.push(curr.label);
                return acc;
            }, [] as string[]);
            const tableData = [
                // 第一行 - 主表头
                headerGroup,
                // 第二行 - 子表头
                header,
                // 数据行
                ...data.map(item => {
                    return header.map(h => {
                        return item?.[h] || '';
                    });
                })
            ];
            // 将数据转换为工作表
            const ws = XLSX.utils.aoa_to_sheet(tableData);
            // 动态计算合并单元格范围 - 创建多层表头效果
            ws['!merges'] = headerGroup.reduce((merges, groupName, colIndex) => {
                if (groupName && groupName !== '') {
                    // 查找相同groupName的连续列
                    let endCol = colIndex;
                    while (endCol + 1 < headerGroup.length
                           && headerGroup[endCol + 1] === '') {
                        endCol++;
                    }
                    if (endCol > colIndex) {
                        merges.push({
                            s: {r: 0, c: colIndex}, // 起始行和列
                            e: {r: 0, c: endCol} // 结束行和列
                        });
                    }
                }
                return merges;
            }, [] as Array<{s: {r: number, c: number}, e: {r: number, c: number}}>);

            // 将工作表添加到工作簿
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
            // 导出Excel文件
            XLSX.writeFile(wb, `${fileName}.xlsx`);
        }
        else {
            // 将数据转换为工作表
            const ws = XLSX.utils.json_to_sheet(data);
            // 创建一个新的工作簿
            const wb = XLSX.utils.book_new();
            // 将工作表添加到工作簿中
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
            // 生成Excel文件
            const excelBuffer = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
            // 使用FileSaver保存文件
            // eslint-disable-next-line max-len
            const blob = new Blob(
                [excelBuffer],
                {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
            );
            saveAs(blob, `${fileName}.xlsx`);
        }
    };
    const actionConfig = useMemo(() => {
        return {
            submitAction: {
                submitFormAction: 'downloadNetwork',
                async $onSubmitRequest({type, way, attributes}: any) {
                    let headers = [] as {label: string, key: any, groupName: string}[];
                    if (type === 'show') {
                        if (way === 'all') {
                            if (isGroup) {
                                // 分组表头处理
                                headers = Object.entries(groupAttributeInfo).flatMap(([groupName, group]) => {
                                    return group.options.map(opt => ({
                                        label: opt.label,
                                        key: opt.value,
                                        groupName
                                    }));
                                });
                            }
                            else {
                                // 无分组表头处理
                                headers = attributeOptions.map(opt => ({
                                    label: opt.label,
                                    key: opt.value,
                                    groupName: ''
                                }));
                            }
                        }
                        else if (way === 'custom') {
                            await form.validateFields();
                            if (isGroup) {
                                // 分组自定义导出
                                headers = Object.entries(groupAttributeInfo).flatMap(([groupName, group]) => {
                                    return group.options
                                        .filter(opt => group.checkedList.includes(opt.value))
                                        .map(opt => ({
                                            label: opt.label,
                                            key: opt.value,
                                            groupName
                                        }));
                                });
                            }
                            else {
                                // 无分组自定义导出
                                headers = attributeOptions
                                    .filter(a => attributeInfo.checkedList.includes(a.value))
                                    .map(opt => ({
                                        label: opt.label,
                                        key: opt.value,
                                        groupName: ''
                                    }));
                            }
                        }
                        const data = dataSource.map(item => {
                            return headers.reduce((obj, header) => {
                                obj[header.label] = item[header.key];
                                return obj;
                            }, {});
                        });
                        isGroup ? downloadExcel(data, fileName, headers) : downloadExcel(data, fileName);
                        hideDialog();
                        throw new Error('');
                    }
                    else if (type === 'all') {
                        actions.exportAll && actions.exportAll();
                        throw new Error('');
                    }
                    return {type, way, attributes};
                },
                $onSubmitResponse(res: any) {
                    if (res.type === 'application/json') {
                        toast.error({
                            message: '导出失败',
                            duration: 5
                        });
                        throw Error('导出失败');
                    }
                    else {
                        exportToExcel(res, fileName);
                        reload();
                        hideDialog();
                    };

                },
                $onSubmitError: (err: any) => {
                    console.log(err, 'err');
                },
                $toastSuccessMessage: '导出成功'
            }
        };
    }, [
        ref, reload, hideDialog, fileName, attributeInfo, attributeOptions,
        dataSource, isGroup, actions, groupAttributeInfo]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleAttributesValidate = useCallback(
        debounce((_, callback) => {
            if (form) {
                const type = form.getFieldValue('type');
                const way = form.getFieldValue('way');
                if (isGroup) {
                    const checkedList = values(groupAttributeInfo).reduce((acc, group) => {
                        return acc.concat(group.checkedList);
                    }, [] as string[]);
                    if (way === 'custom' && checkedList.length === 0) {
                        callback('自定义导出, 请至少选择一个导出项');
                        toast.error({
                            message: '自定义导出, 请至少选择一个导出项',
                            duration: 5
                        });
                        return;
                    }
                }
                else {
                    if (type === 'show') {
                        if (way === 'custom' && attributeInfo.checkedList.length === 0) {
                            callback('自定义导出, 请至少选择一个导出项');
                            toast.error({
                                message: '自定义导出, 请至少选择一个导出项',
                                duration: 5
                            });
                            return;
                        }
                    }
                }
            }
            callback();
        }, 500),
        [attributeInfo, isGroup, groupAttributeInfo]
    );
    const checkAttributes = useCallback(
        (_, value, callback) => {
            handleAttributesValidate(value, callback);
        },
        [handleAttributesValidate]
    );

    const customFunctions = useMemo(
        () => ({checkAttributes, onCheckAllChangeAttributes, onChangeAttributes, onCheckAllChangeGruop, onChangeGroup}),
        [checkAttributes, onCheckAllChangeAttributes, onChangeAttributes, onCheckAllChangeGruop, onChangeGroup]
    );

    const options = useMemo(
        () => ({
            controls,
            formItemOption: {
                noStyle: true
            },
            actionConfig,
            customFunctions
        }),
        [actionConfig, customFunctions]
    );

    const {form, formProps} = useForm(
        ref,
        {actions: actions, attributeInfo, attributeOptions, isGroup, groupAttributeInfo, selectMode},
        options as any
    );

    return (
        <HooksForm
            className="acud-download-form"
            {...formProps}
            form={form}
        />
    );
};

export default forwardRef(UpdateSceneForm);
